import React, { useState, useEffect, useCallback } from 'react';
import { 
  XMarkIcon, 
  CloudArrowUpIcon,
  DocumentTextIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
  ChevronLeftIcon
} from '@heroicons/react/24/outline';
import ShareRequestModal from './ShareRequestModal';

interface Lead {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

interface DocumentType {
  id: number;
  name: string;
  description?: string;
  maxFileSizeMb: number;
  allowedFileTypes: string[];
}

interface DocumentTypeGroup {
  id: number;
  name: string;
  description?: string;
  color: string;
  document_types: { id: number; name: string }[];
}

interface FileUpload {
  id: string;
  file: File;
  error?: string;
}

interface DocTypeUpload {
  documentTypeId: number;
  files: FileUpload[];
}

interface TaskDocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  lead: Lead;
  documentTypeIds: number[];
  taskId?: number;
}

const UPLOAD_SOURCES = [
  { value: 'manual', label: 'Manual' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'other', label: 'Other' }
];

const generateFileId = () => Math.random().toString(36).substr(2, 9);

const TaskDocumentUploadModal: React.FC<TaskDocumentUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  lead,
  documentTypeIds,
  taskId
}) => {
  const [mode, setMode] = useState<'select' | 'upload'>('select');
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [documentGroups, setDocumentGroups] = useState<DocumentTypeGroup[]>([]);
  const [selectedDocTypeIds, setSelectedDocTypeIds] = useState<number[]>([]);
  const [uploads, setUploads] = useState<DocTypeUpload[]>([]);
  const [uploadSource, setUploadSource] = useState('manual');
  const [autoVerify, setAutoVerify] = useState(false);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<'upload' | 'request' | null>(null);
  const [dragOverDocType, setDragOverDocType] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [createdRequest, setCreatedRequest] = useState<{
    token: string;
    leadName: string;
    leadEmail: string;
    documentTypes: string[];
  } | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchDocumentTypes();
      fetchDocumentGroups();
      setMode('select');
      setSuccess(null);
      setError(null);
      setAutoVerify(false);
      setUploadSource('manual');
      setNote('');
      setSearchTerm('');
    }
  }, [isOpen]);

  const [missingRequiredCount, setMissingRequiredCount] = useState(0);

  useEffect(() => {
    if (documentTypes.length > 0 && documentTypeIds.length > 0) {
      const validIds = documentTypeIds.filter(
        id => documentTypes.some(dt => dt.id === id)
      );
      setSelectedDocTypeIds(validIds);
      // Some required types may no longer exist in the fetched list (e.g. deleted).
      // Track how many so we can warn the user instead of silently dropping them.
      setMissingRequiredCount(documentTypeIds.length - validIds.length);
    }
  }, [documentTypes, documentTypeIds]);

  useEffect(() => {
    if (mode === 'upload' && selectedDocTypeIds.length > 0) {
      setUploads(selectedDocTypeIds.map(id => ({
        documentTypeId: id,
        files: []
      })));
    }
  }, [mode, selectedDocTypeIds]);

  const fetchDocumentTypes = async () => {
    try {
      setLoading(true);
      const response = await import('../services/api').then(m => m.default.get('/documents/types'));
      setDocumentTypes(response.data.data || []);
    } catch (err) {
      console.error('Error fetching document types:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentGroups = async () => {
    try {
      const response = await import('../services/api').then(m => m.default.get('/documents/groups'));
      setDocumentGroups(response.data.data || []);
    } catch (err) {
      console.error('Error fetching document groups:', err);
    }
  };

  const getDocumentType = (id: number): DocumentType | undefined => {
    return documentTypes.find(dt => dt.id === id);
  };

  const toggleDocType = (id: number) => {
    setSelectedDocTypeIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectGroup = (group: DocumentTypeGroup) => {
    const groupTypeIds = group.document_types.map(dt => dt.id);
    setSelectedDocTypeIds(prev => {
      const newIds = new Set(prev);
      groupTypeIds.forEach(id => newIds.add(id));
      return Array.from(newIds);
    });
  };

  const validateFile = (file: File, docType: DocumentType | undefined): string | null => {
    if (!docType) return 'Invalid type';
    const maxSize = (docType.maxFileSizeMb || 10) * 1024 * 1024;
    if (file.size > maxSize) return `>${docType.maxFileSizeMb}MB`;
    
    const ext = file.name.split('.').pop()?.toLowerCase().trim() || '';
    if (!ext) return 'No extension';
    
    let allowedTypes: string[] = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'];
    if (docType.allowedFileTypes) {
      if (Array.isArray(docType.allowedFileTypes)) {
        allowedTypes = docType.allowedFileTypes.map(t => String(t).toLowerCase().replace(/^\./, '').trim());
      } else if (typeof docType.allowedFileTypes === 'string') {
        try {
          const parsed = JSON.parse(docType.allowedFileTypes);
          if (Array.isArray(parsed)) {
            allowedTypes = parsed.map(t => String(t).toLowerCase().replace(/^\./, '').trim());
          }
        } catch {
          allowedTypes = (docType.allowedFileTypes as string).split(',').map(t => t.toLowerCase().replace(/^\./, '').trim());
        }
      }
    }
    
    if (!allowedTypes.includes(ext)) return 'Invalid format';
    return null;
  };

  const handleFiles = (docTypeId: number, newFiles: FileList | File[]) => {
    const docType = getDocumentType(docTypeId);
    const filesToAdd: FileUpload[] = Array.from(newFiles).map(file => ({
      id: generateFileId(),
      file,
      error: validateFile(file, docType) || undefined
    }));
    
    setUploads(prev => prev.map(upload => {
      if (upload.documentTypeId === docTypeId) {
        return { ...upload, files: [...upload.files, ...filesToAdd] };
      }
      return upload;
    }));
  };

  const removeFile = (docTypeId: number, fileId: string) => {
    setUploads(prev => prev.map(upload => {
      if (upload.documentTypeId === docTypeId) {
        return { ...upload, files: upload.files.filter(f => f.id !== fileId) };
      }
      return upload;
    }));
  };

  const handleDragOver = useCallback((e: React.DragEvent, docTypeId: number) => {
    e.preventDefault();
    setDragOverDocType(docTypeId);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOverDocType(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, docTypeId: number) => {
    e.preventDefault();
    setDragOverDocType(null);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFiles(docTypeId, files);
    }
  }, []);

  const hasValidFiles = (): boolean => {
    return uploads.some(u => u.files.some(f => !f.error));
  };

  const handleQuickUpload = async () => {
    if (!hasValidFiles()) return;
    
    setUploading(true);
    setError(null);
    
    try {
      const api = await import('../services/api').then(m => m.default);
      const formData = new FormData();
      formData.append('leadId', lead.id.toString());
      formData.append('source', uploadSource);
      formData.append('autoVerify', autoVerify.toString());
      
      const items: { documentTypeId: number; fileCount: number }[] = [];
      
      for (const upload of uploads) {
        const validFiles = upload.files.filter(f => !f.error);
        if (validFiles.length > 0) {
          items.push({ documentTypeId: upload.documentTypeId, fileCount: validFiles.length });
          validFiles.forEach(f => formData.append('files', f.file));
        }
      }
      
      formData.append('items', JSON.stringify(items));
      await api.post('/documents/direct-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSuccess('upload');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1200);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRequestDocuments = async () => {
    if (selectedDocTypeIds.length === 0) return;
    
    setRequesting(true);
    setError(null);
    
    try {
      const api = await import('../services/api').then(m => m.default);
      
      const requestData = {
        leadId: lead.id,
        documentTypes: selectedDocTypeIds.map(id => ({ documentTypeId: id })),
        note: note || 'Documents requested for task completion'
      };
      
      const response = await api.post('/documents/requests', requestData);
      
      const selectedTypeNames = documentTypes
        .filter(dt => selectedDocTypeIds.includes(dt.id))
        .map(dt => dt.name);
      
      setCreatedRequest({
        token: response.data.data.token,
        leadName: `${lead.firstName} ${lead.lastName}`,
        leadEmail: lead.email,
        documentTypes: selectedTypeNames
      });
      
      setSuccess('request');
      setShowShareModal(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Request failed');
    } finally {
      setRequesting(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  const filteredDocTypes = documentTypes.filter(dt =>
    dt.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <DocumentTextIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Task Documents</h2>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <UserIcon className="h-3.5 w-3.5" />
                  <span>{lead.firstName} {lead.lastName}</span>
                  {lead.email && <span className="text-gray-400">• {lead.email}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {mode === 'upload' && (
                <label className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg cursor-pointer hover:bg-green-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={autoVerify}
                    onChange={(e) => setAutoVerify(e.target.checked)}
                    className="w-4 h-4 text-green-600 border-green-300 rounded focus:ring-green-500"
                  />
                  <span className="text-sm font-medium text-green-700">Auto Verify</span>
                </label>
              )}
              <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Success Message */}
          {success && !showShareModal && (
            <div className="mx-5 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
              <span className="text-green-700 font-medium">
                {success === 'upload' ? 'Documents uploaded successfully!' : 'Document request created!'}
              </span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mx-5 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <ExclamationCircleIcon className="h-6 w-6 text-red-500 flex-shrink-0" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-5">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : mode === 'select' ? (
              <div className="space-y-4">
                {/* Required Documents Badge */}
                {documentTypeIds.length > 0 && (
                  <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <span className="text-sm text-amber-700">
                      <span className="font-medium">{documentTypeIds.length}</span> document type(s) required for task completion. You can add or remove as needed.
                    </span>
                  </div>
                )}

                {/* Warn if some required document types are no longer available */}
                {missingRequiredCount > 0 && (
                  <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                    <span className="text-sm text-red-700">
                      <span className="font-medium">{missingRequiredCount}</span> required document type(s)
                      are no longer available (they may have been deleted) and can’t be requested here.
                      Ask an admin to restore them, or the task’s document requirement may not be fully met.
                    </span>
                  </div>
                )}

                {/* Quick Select Groups */}
                {documentGroups.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Quick Select Groups</label>
                    <div className="flex flex-wrap gap-2">
                      {documentGroups.map(group => (
                        <button
                          key={group.id}
                          onClick={() => selectGroup(group)}
                          className="px-3 py-1.5 text-sm font-medium rounded-full border transition-colors hover:opacity-80"
                          style={{ 
                            backgroundColor: `${group.color}15`, 
                            borderColor: group.color, 
                            color: group.color 
                          }}
                        >
                          {group.name} ({group.document_types.length})
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search */}
                <div>
                  <input
                    type="text"
                    placeholder="Search document types..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Document Types List */}
                <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                  {filteredDocTypes.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">No document types found</div>
                  ) : (
                    filteredDocTypes.map(dt => (
                      <label
                        key={dt.id}
                        className={`flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors ${
                          selectedDocTypeIds.includes(dt.id) ? 'bg-blue-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedDocTypeIds.includes(dt.id)}
                          onChange={() => toggleDocType(dt.id)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <span className="font-medium text-gray-900">{dt.name}</span>
                          {dt.description && (
                            <p className="text-xs text-gray-500 mt-0.5">{dt.description}</p>
                          )}
                        </div>
                        {documentTypeIds.includes(dt.id) && (
                          <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">Required</span>
                        )}
                      </label>
                    ))
                  )}
                </div>

                {/* Selected Count */}
                {selectedDocTypeIds.length > 0 && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium text-blue-600">{selectedDocTypeIds.length}</span> document type(s) selected
                  </div>
                )}

                {/* Note for Request */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Add a note for the document request..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
                  />
                </div>
              </div>
            ) : (
              /* Upload Mode */
              <div className="space-y-4">
                {/* Upload Source */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Source:</label>
                  <select
                    value={uploadSource}
                    onChange={(e) => setUploadSource(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    {UPLOAD_SOURCES.map(src => (
                      <option key={src.value} value={src.value}>{src.label}</option>
                    ))}
                  </select>
                </div>

                {/* Upload Zones */}
                <div className="space-y-3">
                  {uploads.map(upload => {
                    const docType = getDocumentType(upload.documentTypeId);
                    if (!docType) return null;
                    
                    return (
                      <div key={upload.documentTypeId} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">{docType.name}</h4>
                            <p className="text-xs text-gray-500">
                              Max: {docType.maxFileSizeMb}MB
                            </p>
                          </div>
                          {documentTypeIds.includes(docType.id) && (
                            <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">Required</span>
                          )}
                        </div>
                        
                        <div
                          onDragOver={(e) => handleDragOver(e, upload.documentTypeId)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, upload.documentTypeId)}
                          className={`p-4 transition-colors ${
                            dragOverDocType === upload.documentTypeId 
                              ? 'bg-blue-50 border-2 border-dashed border-blue-300' 
                              : 'bg-white'
                          }`}
                        >
                          {upload.files.length > 0 ? (
                            <div className="space-y-2">
                              {upload.files.map(f => (
                                <div 
                                  key={f.id} 
                                  className={`flex items-center justify-between px-3 py-2 rounded-lg ${
                                    f.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
                                  }`}
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    {f.error ? (
                                      <ExclamationCircleIcon className="h-4 w-4 text-red-500 flex-shrink-0" />
                                    ) : (
                                      <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                                    )}
                                    <span className="text-sm truncate">{f.file.name}</span>
                                    <span className="text-xs text-gray-400">{formatFileSize(f.file.size)}</span>
                                    {f.error && <span className="text-xs text-red-500">({f.error})</span>}
                                  </div>
                                  <button
                                    onClick={() => removeFile(upload.documentTypeId, f.id)}
                                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                                  >
                                    <TrashIcon className="h-4 w-4 text-gray-500" />
                                  </button>
                                </div>
                              ))}
                              <label className="flex items-center justify-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                <PlusIcon className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-gray-500">Add more files</span>
                                <input
                                  type="file"
                                  multiple
                                  className="hidden"
                                  onChange={(e) => e.target.files && handleFiles(upload.documentTypeId, e.target.files)}
                                />
                              </label>
                            </div>
                          ) : (
                            <label className="flex flex-col items-center justify-center py-6 cursor-pointer">
                              <CloudArrowUpIcon className="h-8 w-8 text-gray-400 mb-2" />
                              <span className="text-sm text-gray-600">Drop files here or click to upload</span>
                              <input
                                type="file"
                                multiple
                                className="hidden"
                                onChange={(e) => e.target.files && handleFiles(upload.documentTypeId, e.target.files)}
                              />
                            </label>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            {mode === 'select' ? (
              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleRequestDocuments}
                    disabled={selectedDocTypeIds.length === 0 || requesting}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {requesting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span>Requesting...</span>
                      </>
                    ) : (
                      <>
                        <PaperAirplaneIcon className="h-4 w-4" />
                        <span>Request Documents</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setMode('upload')}
                    disabled={selectedDocTypeIds.length === 0}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <CloudArrowUpIcon className="h-4 w-4" />
                    <span>Quick Upload</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={() => setMode('select')}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  <span>Back</span>
                </button>
                <button
                  onClick={handleQuickUpload}
                  disabled={!hasValidFiles() || uploading}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <CloudArrowUpIcon className="h-4 w-4" />
                      <span>Upload Documents</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Share Request Modal */}
      {showShareModal && createdRequest && (
        <ShareRequestModal
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            onSuccess();
            onClose();
          }}
          requestToken={createdRequest.token}
          leadName={createdRequest.leadName}
          leadEmail={createdRequest.leadEmail}
          documentTypes={createdRequest.documentTypes}
        />
      )}
    </>
  );
};

export default TaskDocumentUploadModal;
